package gr.tech.GRAttestation.sections.admin

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaFilter
import com.varabyte.kobweb.silk.components.icons.fa.FaRotateLeft
import gr.tech.GRAttestation.components.PaginationControls
import gr.tech.GRAttestation.components.modals.ConfirmModal
import gr.tech.GRAttestation.components.modals.ModalOverlay
import gr.tech.GRAttestation.components.modals.StoreUserEditModal
import gr.tech.GRAttestation.components.modals.StoreUserStatusModal
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.models.storeusers.STORE_USERS_STATUS
import gr.tech.GRAttestation.models.storeusers.StoreUsersDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.sections.tables.AdminStoreUsersTable
import gr.tech.GRAttestation.util.Constants
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

/*
    StoreUsersPanel
    ---------------
    Pure UI composable — no data fetching or API calls.
    The parent page owns all state and provides data + callbacks.

    Use in store-scoped contexts (Details.kt) or all-stores contexts (users/Index.kt)
    by wiring the callbacks to the appropriate API functions.
*/
@Composable
fun AdminStoreUsersPanel(
    alertManager: AlertManager,
    users: List<StoreUsersDTO>,
    totalCount: Int,
    currentPage: Int,
    basicFilters: BasicTableFilters,
    selectedStatus: STORE_USERS_STATUS,
    onPageChange: (Int) -> Unit,
    onFiltersChange: (BasicTableFilters) -> Unit,
    onStatusChange: (STORE_USERS_STATUS) -> Unit,
    onUpdateUser: (StoreUsersDTO) -> Unit,
    onDeleteUser: (StoreUsersDTO) -> Unit,
    onEditUserStatus: (StoreUsersDTO, STORE_USERS_STATUS) -> Unit,
    filterId: String = "storeUsersPanelFilters",
) {
    val pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT
    val totalPages = (totalCount + pageSize - 1) / pageSize

    var showEditUserStatusModal by remember { mutableStateOf(false) }
    var showEditUserModal by remember { mutableStateOf(false) }

    var currentUser: StoreUsersDTO? by remember { mutableStateOf(null) }
    var currentDeleteUser: StoreUsersDTO? by remember { mutableStateOf(null) }

    var selectedDateColumnLabel by remember { mutableStateOf("Created At") }

    // Edit StoreUser
    ModalOverlay(
        isOpen = showEditUserModal,
        onClose = { showEditUserModal = false },
        content = {
            currentUser?.let { user ->
                StoreUserEditModal(
                    storeUser = user,
                    formError = "",
                    onClose = {
                        currentUser = null
                        showEditUserModal = false
                    },
                    onUpdate = { updatedRequest ->
                        onUpdateUser(updatedRequest)
                        currentUser = null
                        showEditUserModal = false
                    }
                )
            }
        }
    )

    // Edit StoreUser Status
    ModalOverlay(
        isOpen = showEditUserStatusModal,
        onClose = { showEditUserStatusModal = false },
        content = {
            currentUser?.let { user ->
                StoreUserStatusModal(
                    selectedSTATUS = STORE_USERS_STATUS.parseStatus(user.status),
                    isInvite = user.isInvite,
                    onClose = {
                        currentUser = null
                        showEditUserStatusModal = false
                    },
                    onStatusChange = {
                        onEditUserStatus(user, it)
                        currentUser = null
                        showEditUserStatusModal = false
                    }
                )
            }
        }
    )

    // Delete StoreUser
    ModalOverlay(
        isOpen = currentDeleteUser != null,
        onClose = { currentDeleteUser = null },
        content = {
            currentDeleteUser?.let { user ->
                ConfirmModal(
                    message = "Are you sure you want to delete ${user.email}? This action cannot be undone.",
                    confirmText = "Delete",
                    onConfirm = {
                        onDeleteUser(user)
                        currentDeleteUser = null
                    },
                    onClose = { currentDeleteUser = null }
                )
            }
        }
    )

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {}
                    .toAttrs {
                        attr("data-bs-toggle", "collapse")
                        attr("data-bs-target", "#$filterId")
                    }
            ) {
                FaFilter(modifier = Modifier.margin(right = 8.px))
                Text("Filters")
            }

            Spacer()

            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { onPageChange(it) }
            )
        }

        // Collapsable Filters
        Column(
            modifier = Modifier.id(filterId).classNames("collapse")
                .margin(top = 8.px, bottom = 16.px)
                .padding(8.px)
                .backgroundColor(Colors.WhiteSmoke)
                .borderRadius(8.px)
                .fillMaxWidth()
        ) {
            // Date / sort row
            Row(
                modifier = Modifier.fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .toAttrs { attr("data-bs-toggle", "dropdown") }
                    ) { Text(selectedDateColumnLabel) }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick {
                                    onFiltersChange(basicFilters.copy(dateColumn = "createdAt"))
                                    selectedDateColumnLabel = "Created At"
                                }.toAttrs()
                            ) { Text("Created At") }
                        }
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick {
                                    onFiltersChange(basicFilters.copy(dateColumn = "updatedAt"))
                                    selectedDateColumnLabel = "Updated At"
                                }.toAttrs()
                            ) { Text("Updated At") }
                        }
                    }
                }

                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isAscending = true)) }
                            .toAttrs()
                    ) { Text("ASCENDING") }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isAscending = false)) }
                            .toAttrs()
                    ) { Text("DESCENDING") }
                }

                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isBefore = true)) }
                            .toAttrs()
                    ) { Text("BEFORE") }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { onFiltersChange(basicFilters.copy(isBefore = false)) }
                            .toAttrs()
                    ) { Text("AFTER") }
                }

                Input(
                    type = InputType.Date,
                    attrs = Modifier.classNames("form-control").width(200.px)
                        .toAttrs {
                            attr("value", basicFilters.dateString)
                            onChange { onFiltersChange(basicFilters.copy(dateString = it.value)) }
                        }
                )

                Spacer()

                Button(
                    attrs = Modifier.classNames("btn", "btn-secondary")
                        .onClick {
                            onFiltersChange(BasicTableFilters())
                            selectedDateColumnLabel = "Created At"
                        }
                        .toAttrs()
                ) {
                    FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                    Text("Reset")
                }
            }

            // Status filter row
            Row(
                modifier = Modifier.margin(top = 16.px).fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .toAttrs { attr("data-bs-toggle", "dropdown") }
                    ) { Text(selectedStatus.name) }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick { onStatusChange(STORE_USERS_STATUS.DELETED) }.toAttrs()
                            ) { Text(STORE_USERS_STATUS.DELETED.status) }
                        }
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick { onStatusChange(STORE_USERS_STATUS.DISABLED) }.toAttrs()
                            ) { Text(STORE_USERS_STATUS.DISABLED.status) }
                        }
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick { onStatusChange(STORE_USERS_STATUS.ACTIVE) }.toAttrs()
                            ) { Text(STORE_USERS_STATUS.ACTIVE.status) }
                        }
                        Li {
                            A(href = "#", attrs = Modifier.classNames("dropdown-item")
                                .onClick { onStatusChange(STORE_USERS_STATUS.PENDING) }.toAttrs()
                            ) { Text(STORE_USERS_STATUS.PENDING.status) }
                        }
                    }
                }
            }
        }

        AdminStoreUsersTable(
            stores = users,
            onView = { },
            onEdit = { user ->
                currentUser = user
                showEditUserModal = true
            },
            onDelete = { user ->
                currentDeleteUser = user
            },
            onEditStatus = { user ->
                currentUser = user
                showEditUserStatusModal = true
            },
            onRestore = { }
        )

        Row(
            modifier = Modifier.fillMaxWidth().margin(top = 16.px),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { onPageChange(it) }
            )
        }
    }
}
